import { PanelSnapshot, SellerConfigPatch, VipOverridePatch } from './panel_comerciales.types';

const BASE_URL = process.env.REACT_APP_API_CRM_PANEL ?? '';
const API_KEY = process.env.REACT_APP_XAPIKEY_CRM_PANEL ?? '';

export function currentPeriod(): string {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
}

interface ApiResponse<T = unknown> {
    success: boolean;
    code: string;
    message?: string;
    data: T;
}

function headers(token: string): Record<string, string> {
    return {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
        'x-api-key': API_KEY,
    };
}

async function unwrap<T>(res: Response): Promise<T> {
    if (!res.ok) {
        const text = await res.text().catch(() => '');
        throw new Error(`HTTP ${res.status}: ${text}`);
    }
    const json: ApiResponse<T> = await res.json();
    if (!json.success) {
        throw new Error(json.message ?? json.code ?? 'Error del servidor');
    }
    return json.data;
}

const FLOW_TYPE_LABELS: Record<string, string> = {
    renovation: 'Renovación',
    early_repurchase: 'Recompra temprana',
    late_repurchase: 'Recompra tardía',
    recovery: 'Recuperación',
    new_client: 'Nuevo cliente',
    poc: 'POC',
    elite: 'Elite',
    cart: 'Carrito',
};

function inferFlowTypes(snapshot: PanelSnapshot): PanelSnapshot {
    if (snapshot.meta.flowTypes?.length) return snapshot;
    const first = snapshot.commercials[0];
    if (!first) return snapshot;
    const keys = Object.keys(first.specialty);
    return {
        ...snapshot,
        meta: {
            ...snapshot.meta,
            flowTypes: keys.map((k) => ({ key: k, label: FLOW_TYPE_LABELS[k] ?? k })),
        },
    };
}

export const getSnapshot = async (
    token: string,
    period: string = currentPeriod(),
): Promise<PanelSnapshot> => {
    const url = `${BASE_URL}commercial-panel/snapshot?period=${period}&country=MX`;
    const res = await fetch(url, { method: 'GET', headers: headers(token) });
    const snapshot = await unwrap<PanelSnapshot>(res);
    return inferFlowTypes(snapshot);
};

export const saveSellerConfig = async (
    token: string,
    sellerId: number,
    patch: SellerConfigPatch,
): Promise<void> => {
    const url = `${BASE_URL}commercial-panel/sellers/${sellerId}/config?country=MX`;
    const body = { ...patch, period: patch.period ?? currentPeriod() };
    const res = await fetch(url, {
        method: 'PUT',
        headers: headers(token),
        body: JSON.stringify(body),
    });
    await unwrap(res);
};

export const saveVipOverride = async (
    token: string,
    clientId: string,
    patch: VipOverridePatch,
): Promise<void> => {
    const url = `${BASE_URL}commercial-panel/clients/${clientId}/vip?country=MX`;
    const res = await fetch(url, {
        method: 'PUT',
        headers: headers(token),
        body: JSON.stringify(patch),
    });
    await unwrap(res);
};
